import {useCallback} from 'react';
import {useRouter} from 'next/router';
import {classNames} from 'primereact/utils';

import {Button, Icon} from '@kasta-io/components';

import {useApolloClient} from '@apollo/client';

import {WEBPAGE_URL} from '@/constants';
import {useLoginContext} from '@/contexts/LoginProcessContext';
import {useNavigationContext} from '@/contexts/NavigationContext';
import {useUser} from '@/contexts/UserContext';
import useIsTargetMedia from '@/hooks/useIsTargetMedia';
import {signOut} from '@/lib/firebase';

import MainButton from '@/components/ui/buttons/MainButton/MainButton';
import ZenDeskChatButton from '@/components/ui/buttons/ZenDeskChatButton/ZenDeskChatButton';

import s from './OnboardingHeader.module.scss';

export type OnboardingHeaderProps = {
  hideMobileLogo?: boolean;
  isLeftAligned?: boolean;
  isLoginFlow?: boolean;
  goBack?: boolean;
  backRoute?: string;
};

export default function OnboardingHeader({
  isLeftAligned = false,
  isLoginFlow = false,
  goBack = false,
  backRoute,
  hideMobileLogo = false,
}: OnboardingHeaderProps) {
  const apolloClient = useApolloClient();
  const {fbUser} = useUser();
  const {pathname, push, query, back} = useRouter();
  const {lastEntryFeaturePage} = useNavigationContext();
  const {setLoginStep} = useLoginContext();
  const isMobileDevice = useIsTargetMedia('desktop', 'less');

  // goBack will toggle the back button display
  const isBackEnabled = goBack === true;
  const showLogo = (isMobileDevice && !hideMobileLogo) || !isMobileDevice;

  // This goBack handler is used for both login and flows using the onboarding header
  const _goBack = useCallback(() => {
    if (isLoginFlow) {
      setLoginStep?.('enterPhoneOrEmail');
      if (fbUser && !fbUser?.isAnonymous) {
        signOut({apolloClient, omitGoToLogin: true});
      }
      return;
    }

    if (goBack) {
      // In case the component is displaying back button and a back route is provided
      // We go to the defined route keeping the query params
      if (backRoute) {
        const queryParamsString = new URLSearchParams(query as Record<string, string>).toString();
        back();
        return;
      }
      // In case the button is being displayed but not concrete route is provided
      //  we go back in history excluding non-feature pages
      push(lastEntryFeaturePage);
      return;
    }
  }, [
    apolloClient,
    backRoute,
    fbUser,
    goBack,
    isLoginFlow,
    lastEntryFeaturePage,
    back,
    push,
    query,
    setLoginStep,
  ]);

  if (pathname === '/account-blocked') {
    return (
      <header className={s.header} role="onboarding-header">
        <Icon name="kasta" size={isMobileDevice ? 40 : 70} />
      </header>
    );
  }

  const headerClassnames = classNames(s.header, {
    [s.leftAligned]: isLeftAligned,
  });

  const redirectToKaApp = () => {
    push(WEBPAGE_URL);
  };

  return (
    <header className={headerClassnames} role="onboarding-header">
      {isBackEnabled && (
        <div role="back-button">
          <MainButton
            size="ICON"
            leftIcon="chevron-left"
            color="PRIMARY"
            iconFontSize={isMobileDevice ? 22 : 26}
            variant="SECONDARY"
            onClick={_goBack}
          />
        </div>
      )}

      {showLogo && (
        <Button
          ghost
          noPadding
          type="secondary-icon"
          onClick={redirectToKaApp}
          style={
            isBackEnabled
              ? {
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }
              : {}
          }>
          <Icon name="kasta" size={isMobileDevice ? 40 : 70} />
        </Button>
      )}

      <ZenDeskChatButton
        size="small"
        type={isMobileDevice ? 'secondary-icon' : 'primary-icon'}
        invert={!isMobileDevice}
        ghost={isMobileDevice}
      />
    </header>
  );
}
